// Note: these schema are used in the response message schema, and must avoid backend dependencies
import { z } from "zod/v4";

const BaseNodeSchema = z.object({
  id: z.string(),
  name: z.string(),
  position: z.object({ x: z.number(), y: z.number() }).optional(),
});

const ContentItemSchema = z.object({
  key: z.string(),
  value: z.string(),
});

const RootNodeSchema = BaseNodeSchema.extend({
  type: z.literal("root"),
  config: z.object({
    audienceId: z
      .string()
      .describe(
        "The base audience this graph applies to. Must be an existing audience ID.",
      ),
    defaultContent: z.array(ContentItemSchema),
  }),
});

const ContentNodeSchema = BaseNodeSchema.extend({
  type: z.literal("content"),
  config: z.object({
    content: z.array(ContentItemSchema),
  }),
});

export const createPersonalizationGraphSchema = <
  TAudienceFilter extends z.ZodTypeAny,
>(
  conditionsSchema: TAudienceFilter,
) => {
  const AudienceFilterNodeSchema = BaseNodeSchema.extend({
    type: z.literal("audience-filter"),
    config: z.object({
      conditions: z.array(conditionsSchema),
    }),
  });

  return z.object({
    name: z.string(),
    description: z.string(),
    parentModelId: z.string(),
    nodes: z.array(
      z.discriminatedUnion("type", [
        RootNodeSchema,
        AudienceFilterNodeSchema,
        ContentNodeSchema,
      ]),
    ),
    edges: z.array(
      z.object({
        source: z.string(),
        target: z.string(),
      }),
    ),
  });
};

// Generic graph schema (with untyped conditions) - used in response schema
export const PersonalizationGraphSchema = createPersonalizationGraphSchema(
  z.any(),
);

// Infer types for type guards
export type PersonalizationGraph = z.infer<typeof PersonalizationGraphSchema>;
export type PersonalizationNode = PersonalizationGraph["nodes"][number];

export type RootNode = Extract<PersonalizationNode, { type: "root" }>;
export type AudienceFilterNode = Extract<
  PersonalizationNode,
  { type: "audience-filter" }
>;
export type ContentNode = Extract<PersonalizationNode, { type: "content" }>;

// Type guards for backend validation
export const isRootNode = (node: PersonalizationNode): node is RootNode =>
  node.type === "root";

export const isAudienceFilterNode = (
  node: PersonalizationNode,
): node is AudienceFilterNode => node.type === "audience-filter";

export const isContentNode = (node: PersonalizationNode): node is ContentNode =>
  node.type === "content";
